import { action, computed, makeObservable, observable } from "mobx";
import RootStore from "store/rootStore";
import IncidentType from "types/IncidentType";
import constants from "tools/constants";
import { isBatchReadOnly } from "tools/isBatchReadOnly";
import { formatTime } from "pages/labelerVideo/utils/formatTime";
import { videoLabelsToIncidents } from "pages/labelerVideo/utils/videoLabelsToIncidents";
import { extractVideoLabelsFromLatestModelPrediction } from "tools/modelPredictions";

const SAVED_FEEDBACK_DURATION_MS = 1500;

class LabelVideoStore {
  @observable incidents: IncidentType[] = [];
  @observable predictionIncidents: IncidentType[] = [];
  @observable currentFrame: number = 0;
  @observable totalFrames: number = 0;
  @observable fps: number = 25;
  @observable currentTime: number = 0;
  @observable duration: number = 0;
  @observable incidentClasses: any[] = [];
  @observable incidentClassesLoading: boolean = false;
  @observable clickedIncident: string | null = null;
  @observable projectId: number | null = null;
  @observable motifs: any[] = [];
  @observable videoMotif: string | null = null;

  @observable videos: any[] = [];
  @observable tags: any[] = [];
  @observable dimensions: { height: number; width: number } = {
    height: 0,
    width: 0,
  };
  @observable nomBatch: string = "";
  @observable batchState: string | null = null;

  @observable isLoadingSave: boolean = false;
  @observable justSaved: boolean = false;
  @observable saveFeedbackMessage: string = "";
  @observable isError: boolean = false;
  @observable message: string = "";

  @observable finishDialogOpen: boolean = false;
  @observable incompleteDialogOpen: boolean = false;
  @observable firstUnlabeledIndex: number | null = null;

  constructor(private store: RootStore) {
    makeObservable(this);
    this.initIncidents();
  }

  @action initIncidents = () => {
    this.incidents = [];
    this.predictionIncidents = [];
  };

  @computed get classList(): string[] {
    const classNames = [...this.incidents, ...this.predictionIncidents]
      .filter((inc) => inc.class !== undefined && inc.class !== null)
      .map((inc) => inc.class!.name);
    return Array.from(new Set(classNames));
  }

  @computed get isReadOnly(): boolean {
    return isBatchReadOnly(this.batchState);
  }

  getClassMeta = (className: string) =>
    this.incidentClasses?.find((c: any) => c?.name === className);

  @action setIncidents = (incidents: IncidentType[]) => {
    this.incidents = incidents;
  };

  @action setPredictionIncidents = (incidents: IncidentType[]) => {
    this.predictionIncidents = incidents;
  };

  /** Charge les prédictions du modèle le plus récent (modelPredictions sur la vidéo). */
  @action loadPredictionIncidentsFromVideo = (video: any, fps: number) => {
    if (!video) {
      this.predictionIncidents = [];
      return;
    }

    const videoLabels = extractVideoLabelsFromLatestModelPrediction(video);
    const defaultColor =
      constants.colors[0]?.color || "#9e9e9e";

    this.predictionIncidents = videoLabelsToIncidents(
      videoLabels,
      String(video.id),
      fps,
      this.incidentClasses ?? [],
      { idPrefix: "pred-", defaultColor }
    );
  };

  @action setCurrentFrame = (frame: number) => {
    this.currentFrame = frame;
  };

  @action setTotalFrames = (frames: number) => {
    this.totalFrames = frames;
  };

  @action setFps = (fps: number) => {
    this.fps = fps;
  };

  @action setCurrentTime = (time: number) => {
    this.currentTime = time;
  };

  @action setDuration = (duration: number) => {
    this.duration = duration;
  };

  @action setIncidentClasses = (classes: any[]) => {
    this.incidentClasses = classes;
  };

  @action setIncidentClassesLoading = (loading: boolean) => {
    this.incidentClassesLoading = loading;
  };

  @action setClickedIncident = (incident: string | null) => {
    this.clickedIncident = incident;
  };

  @action setProjectId = (projectId: number | null) => {
    this.projectId = projectId;
  };

  @action setMotifs = (motifs: any[]) => {
    this.motifs = motifs;
  };

  @action setVideoMotif = (motif: string | null) => {
    this.videoMotif = motif;
  };

  @action setVideos = (videos: any[]) => {
    this.videos = videos;
  };

  @action setTags = (tags: any[]) => {
    this.tags = tags;
  };

  @action setDimensions = (dimensions: { height: number; width: number }) => {
    this.dimensions = dimensions;
  };

  @action setNomBatch = (nomBatch: string) => {
    this.nomBatch = nomBatch;
  };

  @action setBatchState = (batchState: string | null) => {
    this.batchState = batchState;
  };

  @action setIsLoadingSave = (loading: boolean) => {
    this.isLoadingSave = loading;
  };

  @action setJustSaved = (saved: boolean) => {
    this.justSaved = saved;
  };

  @action setSaveFeedbackMessage = (message: string) => {
    this.saveFeedbackMessage = message;
  };

  @action setIsError = (isError: boolean) => {
    this.isError = isError;
  };

  @action setMessage = (message: string) => {
    this.message = message;
  };

  @action setFinishDialogOpen = (open: boolean) => {
    this.finishDialogOpen = open;
  };

  @action setIncompleteDialogOpen = (open: boolean) => {
    this.incompleteDialogOpen = open;
  };

  @action setFirstUnlabeledIndex = (index: number | null) => {
    this.firstUnlabeledIndex = index;
  };

  @action fetchVideos = async (appFetch: any, noBatch: string) => {
    try {
      this.setIsError(false);
      const videos = await appFetch(
        `/api/images/batches/${noBatch}/all?offset=0&size=100`
      );
      this.setVideos(videos);
    } catch (e) {
      this.setIsError(true);
      // @ts-ignore
      this.setMessage(e.message);
    }
  };

  @action fetchBatch = async (appFetch: any, noBatch: string) => {
    try {
      this.setIsError(false);
      const batch = await appFetch(
        `${constants.paths.BATCHES_PATH}/${noBatch}`
      );
      const labelingTypes = batch.project.labelingTypes[0];
      const rawTags: any[] = labelingTypes.listTagsDto || [];
      const LABEL_COLORS = constants.colors.map((c) => c.color);
      const classesWithColors = rawTags.map((tag: any, index: number) => ({
        id: typeof tag === "string" ? index : tag.id,
        name: typeof tag === "string" ? tag : tag.name,
        color: LABEL_COLORS[index % LABEL_COLORS.length],
        icon: null,
      }));
      this.setIncidentClasses(classesWithColors);
      this.setProjectId(batch.project.id);
      const tagsWithColors = rawTags.map((tag: any, index: number) => ({
        ...constants.colors[index],
        legend: typeof tag === "string" ? tag : tag.name,
        visible: true,
      }));
      this.setTags(tagsWithColors);
      this.setMotifs(
        labelingTypes.reasons || ["UNREADABLE", "CROPPED", "OCCLUSION"]
      );
      this.setDimensions({ height: batch.maxHeight, width: batch.maxWidth });
      this.setNomBatch(batch.name);
      this.setBatchState(batch.state);
    } catch (e) {
      this.setIsError(true);
      // @ts-ignore
      this.setMessage(e.message);
    }
  };

  @action fetchInitialData = async (appFetch: any, noBatch: string) => {
    await Promise.all([
      this.fetchVideos(appFetch, noBatch),
      this.fetchBatch(appFetch, noBatch),
    ]);
  };

  private buildVideoLabelsPayload = () =>
    this.incidents.map((incident) => ({
      startTime: formatTime(incident.startFrame, this.fps || 0),
      endTime: formatTime(
        incident.endFrame || incident.startFrame,
        this.fps || 0
      ),
      tagId: incident.class.id,
      tagName: incident.class.name,
    }));

  private isVideoLabeled = (video: any) =>
    (video.labels?.videoLabels?.length || 0) > 0 || video.motif !== null;

  private handlePostVideoNavigation = (
    history: any,
    noBatch: string,
    currentVideoIndex: number,
    updatedVideos: any[]
  ) => {
    const isLastVideo = currentVideoIndex === updatedVideos.length - 1;
    if (!isLastVideo) {
      history.push(`/labelerVideo/${noBatch}/${currentVideoIndex + 1}`);
      return;
    }

    const allLabeled = updatedVideos.every((v) => this.isVideoLabeled(v));
    if (allLabeled) {
      this.setFinishDialogOpen(true);
      return;
    }

    const firstUnlabeled = updatedVideos.findIndex(
      (v) => (v.labels?.videoLabels?.length || 0) === 0 && !v.motif
    );
    this.setFirstUnlabeledIndex(firstUnlabeled);
    this.setIncompleteDialogOpen(true);
  };

  private showSaveFeedbackAndNavigate = (
    history: any,
    noBatch: string,
    currentVideoIndex: number,
    updatedVideos: any[],
    feedbackMessage: string
  ) => {
    this.setSaveFeedbackMessage(feedbackMessage);
    this.setJustSaved(true);

    setTimeout(() => {
      this.setJustSaved(false);
      this.setSaveFeedbackMessage("");
      this.setIsLoadingSave(false);
      this.handlePostVideoNavigation(
        history,
        noBatch,
        currentVideoIndex,
        updatedVideos
      );
    }, SAVED_FEEDBACK_DURATION_MS);
  };

  @action skipVideo = async (
    appFetch: any,
    history: any,
    noBatch: string,
    currentVideoIndex: number,
    motif: string,
    userLogin?: string | null
  ) => {
    if (this.isReadOnly) return;
    const currentVideo = this.videos[currentVideoIndex];
    if (!currentVideo) {
      return;
    }

    this.setIsLoadingSave(true);

    try {
      this.setIsError(false);
      await appFetch(constants.paths.IMAGES_PATH, {
        method: "PUT",
        body: JSON.stringify({
          id: currentVideo.id,
          motif,
          labels: { videoLabels: [] },
          lastLabeledBy: null,
        }),
      });

      const updatedVideos = this.videos.map((v, i) =>
        i === currentVideoIndex
          ? {
              ...v,
              motif,
              labels: { videoLabels: [] },
              lastLabeledBy: userLogin || v.lastLabeledBy,
            }
          : v
      );
      this.setVideos(updatedVideos);
      this.setIncidents([]);
      this.setVideoMotif(motif);

      const isLastVideo = currentVideoIndex === updatedVideos.length - 1;
      this.showSaveFeedbackAndNavigate(
        history,
        noBatch,
        currentVideoIndex,
        updatedVideos,
        isLastVideo
          ? "Vidéo passée"
          : "Vidéo passée — passage à la vidéo suivante…"
      );
    } catch (e) {
      this.setIsError(true);
      // @ts-ignore
      this.setMessage(e.message);
      this.setIsLoadingSave(false);
    }
  };

  @action saveLabels = async (
    appFetch: any,
    history: any,
    noBatch: string,
    currentVideoIndex: number,
    userLogin?: string | null
  ) => {
    if (this.isReadOnly) return;
    const currentVideo = this.videos[currentVideoIndex];
    if (!currentVideo) {
      return;
    }

    this.setIsLoadingSave(true);
    const newVideoDatas = this.buildVideoLabelsPayload();

    try {
      this.setIsError(false);
      await appFetch(constants.paths.IMAGES_PATH, {
        method: "PUT",
        body: JSON.stringify({
          id: currentVideo.id,
          labels: { videoLabels: newVideoDatas },
          motif: null,
          lastLabeledBy: null,
        }),
      });

      const updatedVideos = this.videos.map((v, i) =>
        i === currentVideoIndex
          ? {
              ...v,
              labels: { videoLabels: newVideoDatas },
              motif: null,
              lastLabeledBy: userLogin || v.lastLabeledBy,
            }
          : v
      );
      this.setVideos(updatedVideos);
      this.setVideoMotif(null);

      const isLastVideo = currentVideoIndex === updatedVideos.length - 1;
      this.showSaveFeedbackAndNavigate(
        history,
        noBatch,
        currentVideoIndex,
        updatedVideos,
        isLastVideo
          ? "Vidéo labélisée"
          : "Vidéo labélisée — passage à la vidéo suivante…"
      );
    } catch (e) {
      this.setIsError(true);
      // @ts-ignore
      this.setMessage(e.message);
      this.setIsLoadingSave(false);
    }
  };

  @action getIncidentClasses = async (projectId: number) => {
    this.setIncidentClassesLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${projectId}/video-labels`
      );
      this.setIncidentClasses(response as any);
    } catch (error) {
      console.error(error);
    } finally {
      this.setIncidentClassesLoading(false);
    }
  };
}

export default LabelVideoStore;
